import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  ArrowLeft, PaintBucket, Brush, Eraser, Sticker as StickerIcon,
  Undo2, Redo2, Trash2, Download, Save, PartyPopper, Volume2, VolumeX,
} from "lucide-react";
import { toast } from "sonner";
import { STICKERS, STICKERS_BY_ID, STICKER_IDS } from "@/data/stickers";
import { fetchCustomPages, completePage, saveArtwork } from "@/lib/api";
import { sfx, isMuted, setMuted } from "@/lib/sounds";

const W = 800;
const H = 1000;

const PALETTE = [
  "#FF7A00", "#FFA24D", "#FFD23F", "#39FF14",
  "#7FF0D0", "#2EC5FF", "#2B4EFF", "#8A4FFF",
  "#4B2A85", "#FF5CA8", "#E63329", "#8B4A2F",
  "#B9B4C7", "#16121F", "#9EF01A", "#FFFDF8",
];

function hexToRgb(hex) {
  const n = parseInt(hex.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function floodFill(ctx, sx, sy, hex) {
  const w = ctx.canvas.width;
  const h = ctx.canvas.height;
  sx = Math.round(sx);
  sy = Math.round(sy);
  if (sx < 0 || sy < 0 || sx >= w || sy >= h) return false;
  const img = ctx.getImageData(0, 0, w, h);
  const d = img.data;
  const at = (x, y) => (y * w + x) * 4;
  const si = at(sx, sy);
  const sr = d[si], sg = d[si + 1], sb = d[si + 2];
  const startLum = 0.299 * sr + 0.587 * sg + 0.114 * sb;
  if (startLum < 110) return false; // tapped on a dark outline
  const [fr, fg, fb] = hexToRgb(hex);
  if (Math.abs(sr - fr) + Math.abs(sg - fg) + Math.abs(sb - fb) < 12) return false;
  const tol = 4100; // squared RGB distance, ~64 per channel
  const ok = (i) => {
    const dr = d[i] - sr, dg = d[i + 1] - sg, db = d[i + 2] - sb;
    if (dr * dr + dg * dg + db * db > tol) return false;
    const L = 0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2];
    return L >= 110; // never cross dark outlines
  };
  const seen = new Uint8Array(w * h);
  const stack = [[sx, sy]];
  while (stack.length) {
    const [cx, cy] = stack.pop();
    let lx = cx;
    while (lx > 0 && ok(at(lx - 1, cy))) lx--;
    let rx = cx;
    while (rx < w - 1 && ok(at(rx + 1, cy))) rx++;
    for (let x = lx; x <= rx; x++) {
      const i = at(x, cy);
      d[i] = fr;
      d[i + 1] = fg;
      d[i + 2] = fb;
      d[i + 3] = 255;
      seen[cy * w + x] = 1;
      for (const ny of [cy - 1, cy + 1]) {
        if (ny < 0 || ny >= h) continue;
        if (!seen[ny * w + x] && ok(at(x, ny))) {
          seen[ny * w + x] = 1;
          stack.push([x, ny]);
        }
      }
    }
  }
  ctx.putImageData(img, 0, 0);
  return true;
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

function Confetti() {
  const pieces = Array.from({ length: 46 }, (_, i) => ({
    id: i,
    x: Math.random() * 100,
    delay: Math.random() * 0.8,
    dur: 1.6 + Math.random() * 1.4,
    color: PALETTE[i % 10],
    size: 6 + Math.random() * 8,
    rot: Math.random() * 360,
  }));
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden">
      {pieces.map((p) => (
        <motion.span
          key={p.id}
          className="absolute top-0 block rounded-sm"
          style={{ left: `${p.x}%`, width: p.size, height: p.size * 0.5, background: p.color }}
          initial={{ y: -30, opacity: 1, rotate: p.rot }}
          animate={{ y: "105vh", opacity: [1, 1, 0.4], rotate: p.rot + 540 }}
          transition={{ duration: p.dur, delay: p.delay, ease: "easeIn" }}
        />
      ))}
    </div>
  );
}

export default function ImageStudio({
  routePageId,
  staticImage = null,
  staticTitle = "My Page",
  staticPageId = null,
}) {
  const navigate = useNavigate();
  const [page, setPage] = useState(null);
  const [ready, setReady] = useState(false);
  const [missing, setMissing] = useState(false);
  const [tool, setTool] = useState("fill");
  const [color, setColor] = useState(PALETTE[0]);
  const [brushSize, setBrushSize] = useState(14);
  const [stickerId, setStickerId] = useState(STICKER_IDS[0]);
  const [stickerSize, setStickerSize] = useState(1);
  const [stickers, setStickers] = useState([]);
  const [sparks, setSparks] = useState([]);
  const [muted, setM] = useState(isMuted());
  const [clearArmed, setClearArmed] = useState(false);
  const [workCount, setWorkCount] = useState(0);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [saved, setSaved] = useState(false);

  const baseRef = useRef(null);
  const strokeRef = useRef(null);
  const wrapRef = useRef(null);
  const historyRef = useRef([]);
  const futureRef = useRef([]);
  const drawingRef = useRef(null);

  const canFinish = workCount >= 8;

  useEffect(() => {
    if (staticImage) {
      let dead = false;
      (async () => {
        setPage({ id: routePageId, title: staticTitle, image: staticImage });
        try {
          const img = await loadImage(staticImage);
          if (dead) return;
          const bctx = baseRef.current.getContext("2d");
          bctx.fillStyle = "#FFFDF8";
          bctx.fillRect(0, 0, W, H);
          const r = Math.min(W / img.width, H / img.height);
          const w = img.width * r;
          const h = img.height * r;
          bctx.drawImage(img, (W - w) / 2, (H - h) / 2, w, h);
          setReady(true);
        } catch (e) {
          setMissing(true);
        }
      })();
      return () => {
        dead = true;
      };
    }
    const cid = routePageId.replace(/^custom-/, "");
    let cancelled = false;
    fetchCustomPages()
      .then(async (list) => {
        const item = list.find((i) => i.id === cid);
        if (!item) {
          setMissing(true);
          return;
        }
        if (cancelled) return;
        setPage(item);
        const img = await loadImage(item.image);
        if (cancelled) return;
        const bctx = baseRef.current.getContext("2d");
        bctx.fillStyle = "#FFFDF8";
        bctx.fillRect(0, 0, W, H);
        const r = Math.min(W / img.width, H / img.height);
        const w = img.width * r;
        const h = img.height * r;
        bctx.drawImage(img, (W - w) / 2, (H - h) / 2, w, h);
        setReady(true);
      })
      .catch(() => setMissing(true));
    return () => {
      cancelled = true;
    };
  }, [routePageId, staticImage, staticTitle]);

  useEffect(() => {
    if (missing) {
      toast.error("That page vanished! Back to the vault");
      navigate("/", { replace: true });
    }
  }, [missing, navigate]);

  const snapshot = () => ({
    base: baseRef.current.toDataURL("image/png"),
    strokes: strokeRef.current.toDataURL("image/png"),
    stickers: stickers.map((s) => ({ ...s })),
  });

  const restore = async (s) => {
    setStickers(s.stickers);
    const bctx = baseRef.current.getContext("2d");
    const sctx = strokeRef.current.getContext("2d");
    sctx.clearRect(0, 0, W, H);
    bctx.clearRect(0, 0, W, H);
    bctx.drawImage(await loadImage(s.base), 0, 0);
    sctx.drawImage(await loadImage(s.strokes), 0, 0);
  };

  const pushHistory = () => {
    historyRef.current.push(snapshot());
    if (historyRef.current.length > 25) historyRef.current.shift();
    futureRef.current = [];
  };

  const undo = () => {
    if (!historyRef.current.length) return;
    futureRef.current.push(snapshot());
    restore(historyRef.current.pop());
    sfx.click();
  };

  const redo = () => {
    if (!futureRef.current.length) return;
    historyRef.current.push(snapshot());
    restore(futureRef.current.pop());
    sfx.click();
  };

  const addSpark = (clientX, clientY, c) => {
    const rect = baseRef.current.getBoundingClientRect();
    const id = `${Date.now()}-${Math.random()}`;
    setSparks((s) => [...s, { id, x: clientX - rect.left, y: clientY - rect.top, c }]);
    setTimeout(() => setSparks((s) => s.filter((sp) => sp.id !== id)), 700);
  };

  const getPos = (e) => {
    const rect = baseRef.current.getBoundingClientRect();
    return {
      fx: (e.clientX - rect.left) / rect.width,
      fy: (e.clientY - rect.top) / rect.height,
    };
  };

  const onPointerDown = (e) => {
    if (!ready) return;
    const { fx, fy } = getPos(e);
    const x = fx * W;
    const y = fy * H;
    if (tool === "fill") {
      pushHistory();
      const ok = floodFill(baseRef.current.getContext("2d"), x, y, color);
      if (ok) {
        sfx.pop();
        setWorkCount((n) => n + 1);
        addSpark(e.clientX, e.clientY, color);
      } else {
        historyRef.current.pop();
        sfx.click();
      }
    } else if (tool === "brush") {
      pushHistory();
      drawingRef.current = { last: { x, y }, erase: false };
      const sctx = strokeRef.current.getContext("2d");
      sctx.globalCompositeOperation = "source-over";
      sctx.strokeStyle = color;
      sctx.lineWidth = brushSize;
      sctx.lineCap = "round";
      sctx.lineJoin = "round";
      sctx.beginPath();
      sctx.moveTo(x, y);
      sctx.lineTo(x + 0.01, y + 0.01);
      sctx.stroke();
      sfx.scribble();
      wrapRef.current.setPointerCapture(e.pointerId);
    } else if (tool === "eraser") {
      pushHistory();
      drawingRef.current = { last: { x, y }, erase: true };
      sfx.click();
      wrapRef.current.setPointerCapture(e.pointerId);
    } else if (tool === "sticker") {
      pushHistory();
      setStickers((s) => [...s, { id: `${Date.now()}`, stickerId, fx, fy, s: stickerSize }]);
      setWorkCount((n) => n + 1);
      sfx.boop();
      addSpark(e.clientX, e.clientY, "#FFD23F");
    }
  };

  const onPointerMove = (e) => {
    const dr = drawingRef.current;
    if (!dr) return;
    const { fx, fy } = getPos(e);
    const x = fx * W;
    const y = fy * H;
    const sctx = strokeRef.current.getContext("2d");
    sctx.lineCap = "round";
    sctx.lineJoin = "round";
    if (dr.erase) {
      sctx.globalCompositeOperation = "destination-out";
      sctx.lineWidth = brushSize * 2.4;
    } else {
      sctx.globalCompositeOperation = "source-over";
      sctx.strokeStyle = color;
      sctx.lineWidth = brushSize;
    }
    sctx.beginPath();
    sctx.moveTo(dr.last.x, dr.last.y);
    sctx.lineTo(x, y);
    sctx.stroke();
    dr.last = { x, y };
  };

  const onPointerUp = () => {
    drawingRef.current = null;
  };

  const clearAll = () => {
    if (!clearArmed) {
      setClearArmed(true);
      sfx.click();
      setTimeout(() => setClearArmed(false), 2200);
      return;
    }
    pushHistory();
    const sctx = strokeRef.current.getContext("2d");
    sctx.clearRect(0, 0, W, H);
    setStickers([]);
    sfx.pop();
    setClearArmed(false);
  };

  const exportPng = async () => {
    const c = document.createElement("canvas");
    c.width = W;
    c.height = H;
    const ctx = c.getContext("2d");
    ctx.fillStyle = "#FFFDF8";
    ctx.fillRect(0, 0, W, H);
    ctx.drawImage(baseRef.current, 0, 0);
    ctx.drawImage(strokeRef.current, 0, 0);
    for (const st of stickers) {
      const node = wrapRef.current.querySelector(`[data-sticker-svg="${st.id}"]`);
      if (!node) continue;
      const data = new XMLSerializer().serializeToString(node);
      try {
        const img = await loadImage("data:image/svg+xml;charset=utf-8," + encodeURIComponent(data));
        const size = W * 0.12 * st.s;
        ctx.drawImage(img, st.fx * W - size / 2, st.fy * H - size / 2, size, size);
      } catch (e) {}
    }
    return c.toDataURL("image/jpeg", 0.9);
  };

  const downloadPng = async () => {
    sfx.click();
    const url = await exportPng();
    const a = document.createElement("a");
    a.href = url;
    a.download = `boo-scribbles-${page.title.replace(/\s+/g, "-").toLowerCase()}.jpg`;
    a.click();
    toast.success("Saved to your device!");
  };

  const saveToGallery = async () => {
    sfx.click();
    try {
      const url = await exportPng();
      await saveArtwork({
        pageId: staticPageId || `custom-${page.id}`,
        title: page.title,
        image: url,
      });
      setSaved(true);
      sfx.sparkle();
      toast.success("Hung in the Haunted Gallery!");
    } catch (e) {
      toast.error("The gallery ghost dropped it — try again");
    }
  };

  const onFinish = async () => {
    if (busy) return;
    setBusy(true);
    sfx.fanfare();
    try {
      const res = await completePage(staticPageId || `custom-${page.id}`);
      setResult(res);
      if (res.new_unlock) setTimeout(() => sfx.unlock(), 900);
    } catch (e) {
      toast.error("Magic hiccup — the reward didn't register");
    }
    setBusy(false);
  };

  const toggleMute = () => {
    const next = !muted;
    setMuted(next);
    setM(next);
    if (!next) sfx.click();
  };

  const toolBtn = (id, icon, label) => (
    <button
      key={id}
      data-testid={`tool-${id}-btn`}
      onClick={() => {
        setTool(id);
        sfx.click();
      }}
      aria-label={label}
      className={`flex h-12 w-12 items-center justify-center rounded-2xl border transition-all md:h-14 md:w-14 ${
        tool === id
          ? "border-pumpkin bg-pumpkin text-ink shadow-glow-orange"
          : "border-white/15 bg-white/5 text-paper/80 hover:border-pumpkin/60 hover:text-pumpkin"
      }`}
    >
      {icon}
    </button>
  );

  return (
    <div data-testid="studio-root" className="flex min-h-screen flex-col bg-ink text-paper">
      <header className="sticky top-0 z-40 flex items-center justify-between gap-3 border-b border-white/10 bg-ink/90 px-3 py-2.5 backdrop-blur-xl sm:px-6">
        <div className="flex min-w-0 items-center gap-3">
          <button
            data-testid="studio-back-btn"
            onClick={() => {
              sfx.click();
              navigate("/");
            }}
            aria-label="Back to pages"
            className="rounded-full border border-white/15 p-2.5 transition-colors hover:border-pumpkin hover:text-pumpkin"
          >
            <ArrowLeft size={18} />
          </button>
          <div className="min-w-0">
            <div className="truncate font-display text-xl leading-none text-pumpkin sm:text-2xl">
              {page ? page.title : "My Page"}
            </div>
            <div className="mt-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-paper/50">
              My Pages
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="studio-mute-btn"
            onClick={toggleMute}
            aria-label={muted ? "Unmute" : "Mute"}
            className="rounded-full border border-white/15 p-2.5 transition-colors hover:border-slime hover:text-slime"
          >
            {muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
          </button>
          <button
            data-testid="studio-finish-btn"
            disabled={!canFinish || busy}
            onClick={onFinish}
            className={`flex items-center gap-2 rounded-full px-4 py-2.5 text-sm font-bold transition-all sm:px-6 ${
              canFinish
                ? "bg-slime text-ink shadow-glow-slime animate-pulseglow hover:scale-105"
                : "cursor-not-allowed bg-white/10 text-paper/40"
            }`}
          >
            <PartyPopper size={16} /> Finish
          </button>
        </div>
      </header>

      <div className="flex flex-1 flex-col-reverse md:flex-row">
        <main className="relative flex flex-1 items-center justify-center p-3 sm:p-6">
          <div
            ref={wrapRef}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            data-testid="studio-canvas-wrap"
            className="relative w-full max-w-[560px] touch-none rounded-3xl bg-[#FFFDF8] p-2 shadow-[0_0_60px_rgba(255,122,0,0.15)]"
          >
            <canvas
              ref={baseRef}
              width={W}
              height={H}
              data-testid="studio-canvas-base"
              className={`block w-full rounded-2xl ${ready ? "" : "animate-pulse opacity-40"}`}
            />
            <canvas
              ref={strokeRef}
              width={W}
              height={H}
              data-testid="studio-canvas-strokes"
              className="absolute inset-2 h-[calc(100%-16px)] w-[calc(100%-16px)] rounded-2xl"
            />
            <div className="pointer-events-none absolute inset-2 overflow-hidden rounded-2xl">
              {stickers.map((st) => (
                <div
                  key={st.id}
                  data-sticker-node={st.id}
                  className={`pointer-events-auto absolute ${tool === "eraser" ? "cursor-pointer" : ""}`}
                  style={{
                    left: `${st.fx * 100}%`,
                    top: `${st.fy * 100}%`,
                    width: `${12 * st.s}%`,
                    transform: "translate(-50%, -50%)",
                  }}
                  onClick={(e) => {
                    if (tool === "eraser") {
                      e.stopPropagation();
                      pushHistory();
                      setStickers((list) => list.filter((x) => x.id !== st.id));
                      sfx.pop();
                    }
                  }}
                >
                  <svg
                    data-sticker-svg={st.id}
                    viewBox="0 0 48 48"
                    xmlns="http://www.w3.org/2000/svg"
                    className="block h-auto w-full drop-shadow-[0_2px_6px_rgba(0,0,0,0.35)]"
                  >
                    {STICKERS_BY_ID[st.stickerId].art}
                  </svg>
                </div>
              ))}
            </div>
            <AnimatePresence>
              {sparks.map((sp) => (
                <motion.span
                  key={sp.id}
                  className="pointer-events-none absolute z-10 h-3 w-3 rounded-full"
                  style={{ left: sp.x - 6, top: sp.y - 6, background: sp.c, boxShadow: `0 0 14px ${sp.c}` }}
                  initial={{ scale: 0.4, opacity: 1 }}
                  animate={{ scale: 3.2, opacity: 0, y: -26 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.65, ease: "easeOut" }}
                />
              ))}
            </AnimatePresence>
            {canFinish && !result && (
              <div className="pointer-events-none absolute -top-3 right-4 rounded-full bg-slime px-3 py-1 text-[11px] font-bold text-ink shadow-glow-slime">
                Ready to claim reward!
              </div>
            )}
          </div>
        </main>

        <aside className="shrink-0 border-t border-white/10 bg-night/60 p-3 md:w-64 md:border-l md:border-t-0">
          <div className="flex items-center gap-2 md:flex-col md:items-stretch md:gap-4">
            <div className="flex gap-2 md:justify-center">
              {toolBtn("fill", <PaintBucket size={22} />, "Paint bucket")}
              {toolBtn("brush", <Brush size={22} />, "Brush tool")}
              {toolBtn("eraser", <Eraser size={22} />, "Eraser tool")}
              {toolBtn("sticker", <StickerIcon size={22} />, "Sticker tool")}
            </div>
            <div className="min-w-0 flex-1 md:flex-none">
              <div
                className="flex gap-1.5 overflow-x-auto rounded-2xl border border-white/10 bg-white/5 p-2 no-scrollbar md:grid md:grid-cols-8 md:overflow-visible"
                data-testid="studio-palette"
              >
                {PALETTE.map((c, i) => (
                  <button
                    key={c}
                    data-testid={`palette-color-${i}`}
                    onClick={() => {
                      setColor(c);
                      if (tool === "eraser" || tool === "sticker") setTool("fill");
                      sfx.click();
                    }}
                    aria-label={`Color ${c}`}
                    className={`h-8 w-8 shrink-0 rounded-full border-2 transition-transform hover:scale-110 ${
                      color === c ? "scale-110 border-slime" : "border-white/20"
                    }`}
                    style={{ background: c }}
                  />
                ))}
              </div>
            </div>
          </div>

          <div className="mt-3 flex items-center gap-3 md:mt-4 md:flex-col md:items-stretch md:gap-3">
            {tool === "sticker" ? (
              <>
                <div className="flex flex-1 gap-1.5 overflow-x-auto rounded-2xl border border-white/10 bg-white/5 p-2 no-scrollbar md:flex-none" data-testid="sticker-picker">
                  {STICKERS.map((st) => (
                    <button
                      key={st.id}
                      data-testid={`sticker-pick-${st.id}`}
                      onClick={() => {
                        setStickerId(st.id);
                        sfx.boop();
                      }}
                      aria-label={`Sticker ${st.name}`}
                      className={`h-9 w-9 shrink-0 rounded-xl border-2 p-0.5 ${
                        stickerId === st.id ? "border-slime" : "border-transparent opacity-70"
                      }`}
                    >
                      <svg viewBox="0 0 48 48" className="h-full w-full">{st.art}</svg>
                    </button>
                  ))}
                </div>
                <label className="flex items-center gap-2 rounded-2xl border border-white/10 bg-white/5 px-3 py-2">
                  <span className="text-[11px] font-semibold uppercase tracking-wider text-paper/60">Big</span>
                  <input
                    data-testid="sticker-size-slider"
                    type="range"
                    min="0.6"
                    max="1.8"
                    step="0.1"
                    value={stickerSize}
                    onChange={(e) => setStickerSize(+e.target.value)}
                    className="w-24 accent-[#39FF14]"
                  />
                </label>
              </>
            ) : (
              <>
                <label className="flex flex-1 items-center gap-2 rounded-2xl border border-white/10 bg-white/5 px-3 py-2 md:flex-none">
                  <span className="text-[11px] font-semibold uppercase tracking-wider text-paper/60">Size</span>
                  <input
                    data-testid="brush-size-slider"
                    type="range"
                    min="4"
                    max="44"
                    value={brushSize}
                    onChange={(e) => setBrushSize(+e.target.value)}
                    className="w-full accent-[#FF7A00]"
                  />
                </label>
                <p className="hidden px-1 text-xs leading-relaxed text-paper/50 md:block">
                  Tap any closed area to flood it with color. The bucket works
                  best on line art with closed shapes!
                </p>
              </>
            )}
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-2 md:mt-4 md:justify-center">
            <button
              data-testid="studio-undo-btn"
              disabled={!historyRef.current.length}
              onClick={undo}
              aria-label="Undo"
              className="flex h-11 w-11 items-center justify-center rounded-2xl border border-white/15 bg-white/5 transition-colors hover:border-pumpkin hover:text-pumpkin disabled:opacity-30"
            >
              <Undo2 size={18} />
            </button>
            <button
              data-testid="studio-redo-btn"
              disabled={!futureRef.current.length}
              onClick={redo}
              aria-label="Redo"
              className="flex h-11 w-11 items-center justify-center rounded-2xl border border-white/15 bg-white/5 transition-colors hover:border-pumpkin hover:text-pumpkin disabled:opacity-30"
            >
              <Redo2 size={18} />
            </button>
            <button
              data-testid="studio-clear-btn"
              onClick={clearAll}
              className={`flex h-11 items-center gap-2 rounded-2xl border px-4 text-sm font-semibold transition-all ${
                clearArmed
                  ? "border-candy bg-candy/15 text-candy"
                  : "border-white/15 bg-white/5 text-paper/80 hover:border-candy hover:text-candy"
              }`}
            >
              <Trash2 size={16} /> {clearArmed ? "Sure?" : "Clear scribbles"}
            </button>
            <button
              data-testid="studio-download-btn"
              onClick={downloadPng}
              className="flex h-11 items-center gap-2 rounded-2xl border border-white/15 bg-white/5 px-4 text-sm font-semibold transition-colors hover:border-slime hover:text-slime"
            >
              <Download size={16} /> Download
            </button>
            <button
              data-testid="studio-save-btn"
              onClick={saveToGallery}
              disabled={saved}
              className="flex h-11 items-center gap-2 rounded-2xl bg-pumpkin px-4 text-sm font-bold text-ink shadow-glow-orange transition-transform hover:scale-105 disabled:opacity-60"
            >
              <Save size={16} /> {saved ? "Saved!" : "Save"}
            </button>
          </div>
        </aside>
      </div>

      <AnimatePresence>
        {result && (
          <motion.div
            data-testid="celebration-modal"
            className="fixed inset-0 z-[90] flex items-center justify-center bg-ink/85 backdrop-blur-md"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <Confetti />
            <motion.div
              initial={{ scale: 0.6, y: 60, opacity: 0 }}
              animate={{ scale: 1, y: 0, opacity: 1 }}
              transition={{ type: "spring", stiffness: 180, damping: 16 }}
              className="relative mx-4 w-full max-w-md rounded-3xl border-2 border-pumpkin/50 bg-night p-8 text-center shadow-frame"
            >
              <div className="font-display text-4xl text-pumpkin text-glow-orange sm:text-5xl">
                Scream-tastic!
              </div>
              <p className="mt-2 text-paper/70">Your {page.title} looks boo-tiful!</p>

              {result.new_sticker && (
                <motion.div
                  initial={{ scale: 0, rotate: -30 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{ delay: 0.4, type: "spring", stiffness: 200, damping: 10 }}
                  className="mx-auto mt-6 flex flex-col items-center gap-2 rounded-2xl border border-slime/40 bg-slime/10 px-8 py-5"
                  data-testid="celebration-sticker"
                >
                  <svg viewBox="0 0 48 48" className="h-20 w-20">
                    {STICKERS_BY_ID[result.new_sticker].art}
                  </svg>
                  <span className="text-sm font-bold text-slime">
                    New sticker: {STICKERS_BY_ID[result.new_sticker].name}!
                  </span>
                </motion.div>
              )}

              {result.new_unlock && (
                <motion.div
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.7 }}
                  className="mt-4 rounded-2xl border-2 border-pumpkin bg-pumpkin/15 px-4 py-3"
                  data-testid="celebration-unlock"
                >
                  <div className="flex items-center justify-center gap-2 font-display text-2xl text-pumpkin">
                    SECRET UNLOCKED!
                  </div>
                  <div className="mt-1 text-sm font-semibold text-paper">
                    {result.new_unlock.name} pages are now open in the Vault
                  </div>
                </motion.div>
              )}

              <div className="mt-7 grid gap-2.5">
                <button
                  data-testid="celebration-save-btn"
                  onClick={saveToGallery}
                  disabled={saved}
                  className="rounded-full bg-pumpkin py-3 font-bold text-ink shadow-glow-orange transition-transform hover:scale-[1.03] disabled:opacity-60"
                >
                  {saved ? "Hung in the Gallery!" : "Save to Haunted Gallery"}
                </button>
                <button
                  data-testid="celebration-download-btn"
                  onClick={downloadPng}
                  className="rounded-full border-2 border-slime/60 py-3 font-semibold text-slime transition-colors hover:bg-slime/10"
                >
                  Download my masterpiece
                </button>
                <div className="flex gap-2.5">
                  <button
                    data-testid="celebration-keep-btn"
                    onClick={() => setResult(null)}
                    className="flex-1 rounded-full border border-white/20 py-3 text-sm font-semibold text-paper/80 transition-colors hover:border-paper/50"
                  >
                    Keep decorating
                  </button>
                  <button
                    data-testid="celebration-done-btn"
                    onClick={() => navigate("/")}
                    className="flex-1 rounded-full border border-white/20 py-3 text-sm font-semibold text-paper/80 transition-colors hover:border-paper/50"
                  >
                    Color another
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
